import { normalizeArticleUrl } from "./source-url.js";

export const WORKSPACE_HISTORY_STATE_KEY = "aiChatWorkspace";

export type WorkspaceContext = Record<string, unknown> & {
  entryPoint?: string;
  query?: string;
  selectedSourceUrl?: string;
  sourceTitle?: string;
};

export type WorkspaceHistoryPayload = {
  kind: string;
  question: string;
  entryPoint: string;
  sourceUrl: string;
  sourceTitle: string;
  sourceNavigationDepth?: number;
  sourceScrollTop?: number;
  historySessionToken: string;
  context: WorkspaceContext;
};

export type WorkspaceStartupInput = {
  url?: string | URL;
  search?: string;
  hash?: string;
  historyState?: unknown;
};

export type WorkspaceStartupOptions = {
  baseUrl?: string;
  normalizeSource?: (source: string) => string;
};

export type WorkspaceStartupState = {
  payload: WorkspaceHistoryPayload | null;
  question: string;
  entryPoint: string;
  questionPayload: WorkspaceHistoryPayload | null;
  sourceUrl: string;
  sourceTitle: string;
  sourceNavigationDepth: number;
  sourceScrollTop: number;
  sourcePayload: WorkspaceHistoryPayload | null;
};

export type WorkspacePayloadOptions = Partial<
  Omit<WorkspaceHistoryPayload, "context">
> & {
  context?: WorkspaceContext;
};

export type WorkspaceHistoryLike = {
  readonly state: unknown;
  pushState(state: unknown, unused: string, url?: string | URL | null): void;
  replaceState(state: unknown, unused: string, url?: string | URL | null): void;
};

const DEFAULT_BASE_URL = "http://localhost/";

export function getWorkspaceHistoryState(
  state: unknown,
): WorkspaceHistoryPayload | null {
  if (!isRecord(state)) return null;
  const payload = state[WORKSPACE_HISTORY_STATE_KEY];
  return isRecord(payload) ? (payload as WorkspaceHistoryPayload) : null;
}

export function getHashSource(hash: string): string {
  const match = (hash || "").match(/(?:^#|&)source=([^&]+)/);
  if (!match?.[1]) return "";

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return "";
  }
}

export function parseWorkspaceStartup(
  input: WorkspaceStartupInput,
  options: WorkspaceStartupOptions = {},
): WorkspaceStartupState {
  const url = createUrl(input.url, options.baseUrl);
  const search = input.search ?? url?.search ?? "";
  const hash = input.hash ?? url?.hash ?? "";
  const searchParams = new URLSearchParams(search);
  const payload = getWorkspaceHistoryState(input.historyState);
  const payloadQuestion = stringValue(payload?.question).trim();
  const q = stringValue(searchParams.get("q")).trim();
  const ask = stringValue(searchParams.get("ask")).trim();
  const historyPayloadQuestion =
    payload?.kind === "history" && payloadQuestion ? payloadQuestion : "";
  const question = historyPayloadQuestion || q || ask || payloadQuestion;
  const questionPayload =
    payload && payloadQuestion && payloadQuestion === question ? payload : null;
  const entryPoint = historyPayloadQuestion
    ? stringValue(payload?.entryPoint, "history-entry")
    : getStartupEntryPoint({ q, ask, payload, question });
  const sourceCandidate = getStartupSourceCandidate({
    searchParams,
    hash,
    payload,
    url,
  });
  const sourceUrl = normalizeStartupSource(sourceCandidate.value, options);
  const sourcePayload =
    sourceCandidate.fromPayload && sourceUrl ? payload : null;

  return {
    payload,
    question,
    entryPoint,
    questionPayload,
    sourceUrl,
    sourceTitle: sourcePayload ? stringValue(sourcePayload.sourceTitle) : "",
    sourceNavigationDepth: sourcePayload
      ? numberValue(sourcePayload.sourceNavigationDepth)
      : 0,
    sourceScrollTop: sourcePayload
      ? numberValue(sourcePayload.sourceScrollTop)
      : 0,
    sourcePayload,
  };
}

export function buildWorkspacePayload(
  options: WorkspacePayloadOptions = {},
): WorkspaceHistoryPayload {
  return buildPayload({ ...options, kind: options.kind ?? "workspace" });
}

export function buildQuestionPayload(
  question: string,
  options: WorkspacePayloadOptions = {},
): WorkspaceHistoryPayload {
  return buildPayload({
    ...options,
    kind: options.kind ?? "question",
    question,
    entryPoint: options.entryPoint ?? contextEntryPoint(options.context),
  });
}

export function buildSourcePayload(
  sourceUrl: string,
  sourceTitle = "",
  options: WorkspacePayloadOptions = {},
): WorkspaceHistoryPayload {
  const context = mergeSourceContext(options.context, sourceUrl, sourceTitle);
  return buildPayload({
    ...options,
    kind: options.kind ?? "source",
    sourceUrl,
    sourceTitle,
    context,
  });
}

export function mergeSourceContext(
  context: WorkspaceContext = {},
  sourceUrl = "",
  sourceTitle = "",
): WorkspaceContext {
  return {
    ...context,
    ...(sourceUrl ? { selectedSourceUrl: sourceUrl } : {}),
    sourceTitle,
  };
}

export function buildSourceAwarePayload(
  sourceUrl: string,
  sourceTitle = "",
  options: WorkspacePayloadOptions = {},
): WorkspaceHistoryPayload {
  const context = mergeSourceContext(options.context, sourceUrl, sourceTitle);
  return buildPayload({
    ...options,
    kind: options.kind ?? (sourceUrl ? "source" : "workspace"),
    question: options.question ?? contextQuery(context),
    entryPoint: options.entryPoint ?? contextEntryPoint(context, "source-url"),
    sourceUrl,
    sourceTitle,
    context,
  });
}

export function pushWorkspaceHistoryState(
  history: WorkspaceHistoryLike,
  url: string | URL,
  payload: WorkspaceHistoryPayload,
): void {
  history.pushState(buildHistoryState(history.state, payload), "", url);
}

export function replaceWorkspaceHistoryState(
  history: WorkspaceHistoryLike,
  url: string | URL,
  payload: WorkspaceHistoryPayload,
): void {
  history.replaceState(buildHistoryState(history.state, payload), "", url);
}

function buildPayload(
  options: WorkspacePayloadOptions,
): WorkspaceHistoryPayload {
  const context = options.context ?? {};
  const payload: WorkspaceHistoryPayload = {
    kind: stringValue(options.kind, "workspace"),
    question: stringValue(options.question),
    entryPoint: stringValue(options.entryPoint, contextEntryPoint(context)),
    sourceUrl: stringValue(options.sourceUrl),
    sourceTitle: stringValue(options.sourceTitle),
    historySessionToken: stringValue(options.historySessionToken),
    context,
  };
  const sourceNavigationDepth = numberValue(options.sourceNavigationDepth);
  if (sourceNavigationDepth > 0) {
    payload.sourceNavigationDepth = sourceNavigationDepth;
  }
  const sourceScrollTop = numberValue(options.sourceScrollTop);
  if (sourceScrollTop > 0) {
    payload.sourceScrollTop = sourceScrollTop;
  }
  return payload;
}

function getStartupEntryPoint(input: {
  q: string;
  ask: string;
  payload: WorkspaceHistoryPayload | null;
  question: string;
}): string {
  if (input.q) return "query-url";
  if (input.ask) return "workspace-url";
  if (input.question)
    return stringValue(input.payload?.entryPoint, "workspace-url");
  return stringValue(input.payload?.entryPoint);
}

function getStartupSourceCandidate(input: {
  searchParams: URLSearchParams;
  hash: string;
  payload: WorkspaceHistoryPayload | null;
  url?: URL | null;
}): { value: string; fromPayload: boolean } {
  const querySource = stringValue(input.searchParams.get("source"));
  if (querySource) return { value: querySource, fromPayload: false };

  const hashSource = getHashSource(input.hash);
  if (hashSource) return { value: hashSource, fromPayload: false };

  if (input.payload?.sourceUrl && input.payload.kind !== "history") {
    return {
      value: stringValue(input.payload.sourceUrl),
      fromPayload: true,
    };
  }

  if (input.url) {
    const normalized = normalizeArticleUrl(input.url.href, input.url);
    if (normalized) {
      return {
        value: normalized.path,
        fromPayload: false,
      };
    }
  }

  return {
    value: "",
    fromPayload: false,
  };
}

function normalizeStartupSource(
  source: string,
  options: WorkspaceStartupOptions,
): string {
  const value = source.trim();
  if (!value) return "";
  if (!options.normalizeSource) return value;

  try {
    return stringValue(options.normalizeSource(value)).trim();
  } catch {
    return "";
  }
}

function createUrl(
  value: string | URL | undefined,
  baseUrl?: string,
): URL | null {
  if (value instanceof URL) return value;
  if (!value) return null;

  try {
    return new URL(value, baseUrl ?? DEFAULT_BASE_URL);
  } catch {
    return null;
  }
}

function buildHistoryState(
  state: unknown,
  payload: WorkspaceHistoryPayload,
): Record<string, unknown> {
  return {
    ...(isRecord(state) ? state : {}),
    [WORKSPACE_HISTORY_STATE_KEY]: payload,
  };
}

function contextEntryPoint(
  context: WorkspaceContext = {},
  fallback = "workspace",
) {
  return stringValue(context.entryPoint, fallback);
}

function contextQuery(context: WorkspaceContext = {}) {
  return stringValue(context.query);
}

function stringValue(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function numberValue(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
